import { __decorate, __metadata } from 'tslib';
import ReactDOM, { createPortal } from 'react-dom';
import { Inject } from '@tencent/ioc';
import React from 'react';
import { WorkbenchConfigurationState } from '../../services/settings/auto-sync-settings.js';
import { BuiltinUiState } from '../../services/store/builtin-state.js';
import { withState } from '../../services/store/with-state.js';
import { SurfaceService } from '../../services/surface/surface-service.js';
import { ErrorBoundary } from '../../utils/error-boundary.js';
import { Widget } from '../widget/widget.js';
import styles from './surface.less.js';
import { EventEmitter } from '../../services/event-emitter/event-emitter.js';
import { BuiltinUIEvent } from '../../services/event-emitter/builtin-event.js';
import { isServer } from '../../utils/env.js';
import { ResizeObserver } from '../../utils/resize-observer.js';
import { SurfaceContext } from '../../services/surface/surface-type.js';
import { evaluate } from '../../controllers/base/evaluate.js';
import { ActionPointRunner } from '../../providers/action-point/action-point-runner.js';

function isNodeContainsOrEqual(node, child) {
    return node.contains(child) || node === child;
}
const SurfaceWrapper = withState([
    WorkbenchConfigurationState.DisableToolbar,
    WorkbenchConfigurationState.ToolbarAlignment,
    WorkbenchConfigurationState.SupportNativeDomScrolling,
    BuiltinUiState.EmbedWidget,
    BuiltinUiState.EmbedWidgetContainer,
], true)(({ children, embedWidget, id, forwardRef, embedWidgetContainer, [WorkbenchConfigurationState.SupportNativeDomScrolling]: domScroll, }) => {
    const widgets = embedWidget
        .filter(widget => widget.surface === id || widget.surface === null)
        .map(widget => React.createElement(Widget, { key: `embed-${widget.name}`, style: widget.styles, visible: true }, widget.component));
    const domScrollingStyle = { overflow: 'auto', scrollbarGutter: 'stable' };
    const style = domScroll ? domScrollingStyle : {};
    return React.createElement("div", { className: styles['editor-wrapper'], ref: forwardRef, style: style },
        children,
        embedWidgetContainer[id] ? createPortal(widgets, embedWidgetContainer[id]) : widgets);
});
class SurfaceComponent extends React.PureComponent {
    constructor(props) {
        super(props);
        this.node = React.createRef();
        this.wrapperNode = React.createRef();
    }
    componentDidMount() {
        this.sensor = new ResizeObserver(() => {
            this.bindResizeCallback();
        });
        this.fireDomChangedNotification();
    }
    componentDidUpdate() {
        this.fireDomChangedNotification();
    }
    getSnapshotBeforeUpdate() {
        if (document.activeElement
            && this.node.current
            && isNodeContainsOrEqual(this.node.current, document.activeElement)) {
            this.props.focusStream.next(true);
        }
        else {
            this.props.focusStream.next(false);
        }
        return null;
    }
    fireDomChangedNotification() {
        if (this.wrapperNode.current) {
            this.sensor.disconnect();
            this.sensor.observe(this.wrapperNode.current);
        }
        if (this.node.current) {
            this.props.stream.next(this.node.current);
        }
        else {
            this.props.stream.error(null);
        }
    }
    componentWillUnmount() {
        this.props.stream.complete();
        this.sensor.disconnect();
        this.props.focusStream.complete();
    }
    bindResizeCallback() {
        let rafId;
        let hasChanged = false;
        const callback = () => {
            var _a;
            const rect = (_a = this.wrapperNode.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (!rect) {
                return;
            }
            const { width, height } = rect;
            if (hasChanged
                && !this.isWrapperSizeChanged(width, height)) {
                this.eventEmitter.emit(BuiltinUIEvent.SurfaceSizeChanged);
                hasChanged = false;
                if (rafId) {
                    window.cancelAnimationFrame(rafId);
                    rafId = undefined;
                }
            }
            else if (this.isWrapperSizeChanged(width, height)) {
                hasChanged = true;
                rafId = window.requestAnimationFrame(callback);
            }
            this.lastTimeRect = rect;
        };
        window.requestAnimationFrame(callback);
    }
    isWrapperSizeChanged(width, height) {
        return !this.lastTimeRect || this.lastTimeRect.width !== width || this.lastTimeRect.height !== height;
    }
    getWrapper(wrappers) {
        var _a, _b;
        const [outer, ...inner] = wrappers;
        if (!outer)
            return null;
        if (((_a = outer.surface) !== null && _a !== void 0 ? _a : this.props.id) !== this.props.id) {
            return this.getWrapper(inner);
        }
        return React.createElement(ErrorBoundary, null, React.cloneElement(outer.component, Object.assign(Object.assign({}, outer.component.props), { key: `wrapper-${outer.name}`, children: (_b = this.getWrapper(inner)) !== null && _b !== void 0 ? _b : outer.component.props.children })));
    }
    render() {
        const surfaceWrapper = {
            name: 'surface-wrapper',
            priority: 0,
            component: React.createElement(SurfaceWrapper, { id: this.props.id, forwardRef: this.wrapperNode }),
        };
        const serverHtml = isServer && this.props.ssrChildren ? { __html: this.props.ssrChildren } : undefined;
        const surfaceChild = {
            name: 'surface-child',
            priority: -Infinity,
            component: React.createElement("div", { id: this.props.id, ref: this.node, style: { minHeight: '100%', height: '100%', width: '100%' }, onContextMenu: (e) => {
                    if (e.currentTarget.contains(e.target)) {
                        this.eventEmitter.emit(BuiltinUIEvent.BeforeContextMenu, e);
                    }
                } }),
        };
        if (serverHtml) {
            surfaceChild.component = React.cloneElement(surfaceChild.component, { dangerouslySetInnerHTML: serverHtml });
        }
        const stackClasses = [styles['stack-view']];
        if (!this.props.show) {
            stackClasses.push(styles.hide);
        }
        const child = React.createElement("div", { className: stackClasses.join(' ') },
            React.createElement(SurfaceContext.Provider, { value: {
                    $grid: this.props.grid,
                    $surface: this.props.id,
                    evaluateWithContext: (config, defaultValue, ...args) => {
                        if (config instanceof ActionPointRunner) {
                            const surfaceEditor = this.surfaceService.getEditor(this.props.id);
                            config.setContext({ editor: surfaceEditor });
                        }
                        return evaluate(config, defaultValue, ...args);
                    },
                } }, this.getWrapper(this.props.wrapWidget.concat(surfaceWrapper, surfaceChild))));
        return this.props.targetDom ? ReactDOM.createPortal(child, this.props.targetDom) : child;
    }
}
__decorate([
    Inject(),
    __metadata("design:type", EventEmitter)
], SurfaceComponent.prototype, "eventEmitter", void 0);
__decorate([
    Inject(),
    __metadata("design:type", SurfaceService)
], SurfaceComponent.prototype, "surfaceService", void 0);
const Surface = withState([BuiltinUiState.WrapWidget], true)(SurfaceComponent);

export { Surface };
//# sourceMappingURL=surface.js.map
